/**
 * Governance Config Store — Persisted Policy Overrides
 *
 * RESOLVES: CONFLICT-03 (Governance Policy Settings Not Persisted)
 *
 * Stores governance policy overrides via dataLayer so that
 * GovernanceSettingsTab customizations survive page reloads.
 *
 * The policyEngine reads from this store at initialization,
 * merging overrides with defaultPolicy.
 *
 * Fase 2 dataLayer migration: localStorage calls replaced by dataLayer.
 */

import { debug, warn } from '../../utils/logger'
import { dataLayer } from '../../data/dataLayer'
import { STAGES } from './stageDefinitions'

// ─── Types ────────────────────────────────────────────────────────────────────

/**
 * Staged enforcement mode for a governance scope (FG4 §F0.C).
 * - 'off'     — rule not evaluated.
 * - 'shadow'  — rule evaluated, findings emitted to shadowFindingsStore, NEVER blocks.
 * - 'warn'    — rule evaluated, surfaced as a non-blocking warning.
 * - 'enforce' — rule evaluated, blocks on failure.
 *
 * Anti-bandaid principle #4: every scope ramps off → shadow → warn → enforce.
 */
export type EnforcementMode = 'off' | 'shadow' | 'warn' | 'enforce'

/**
 * Grace ledger entry (FG4 §F0.C). A NAMED, COUNTABLE, OWNED exception that
 * temporarily degrades severity for a scope until `until`. Replaces implicit
 * `return OK` permissiveness — permissiveness becomes a documented debt with
 * an expiry date (anti-bandaid principle #3).
 */
export interface GraceLedgerEntry {
  /** Scope this grace applies to. At least one field should be set. */
  scope: { module?: string; family?: string; questionId?: string }
  /** ISO date string. After this date the grace no longer applies. */
  until: string
  /** Why this grace exists. */
  reason: string
  /** Who owns closing this debt. */
  owner: string
  /** Severity to degrade matching findings to while grace is active (default 50). */
  degradeToSeverity?: number
}

export interface GovernanceConfigOverrides {
  /** Schema version for migration safety */
  version: 1

  /** Quality gate overrides */
  qualityGates?: {
    minCogLevel?: number
    maxCogLevel?: number
    minDidacticDepth?: number
    minQualityScore?: number
  }

  /** Pipeline stage toggles */
  pipelineStages?: {
    schemaEnabled?: boolean
    typeEnabled?: boolean
    contentEnabled?: boolean
    supportEnabled?: boolean
    visualEnabled?: boolean
    graphEnabled?: boolean
    pedagogyEnabled?: boolean
  }

  /** Pipeline behavior */
  pipelineBehavior?: {
    strictMode?: boolean
    autoBlock?: boolean
  }

  /** Policy overrides */
  policy?: {
    minQuestionsPerSkill?: number
    maxQuestionsPerSkill?: number
    requireHints?: boolean
    requireExplanation?: boolean
    requireVisuals?: boolean
    distributionCheck?: boolean
  }

  /**
   * Staged enforcement (FG4 §F0.C). Replaces the single global
   * `enforcePedagogicalContract: boolean` with per-module / per-type modes.
   * Backward-compatible: when omitted, callers fall back to the legacy
   * global behaviour (pedagogy off by default).
   */
  enforcement?: {
    /** Per-module pedagogical-contract enforcement mode (key = module code). */
    pedagogical?: Record<string, EnforcementMode>
    /** Per-question-type strict-schema enforcement mode (key = QuestionType). */
    schemas?: Record<string, EnforcementMode>
  }

  /** Grace ledger — named, owned, expiring exceptions (FG4 §F0.C). */
  graceLedger?: GraceLedgerEntry[]

  /** Notification preferences */
  notifications?: {
    notifyBlockers?: boolean
    notifyWarnings?: boolean
    notifyWeeklyReport?: boolean
    notifyNewRules?: boolean
  }

  /** System settings */
  system?: {
    cacheEnabled?: boolean
    debugMode?: boolean
    autoValidateOnSave?: boolean
    batchSize?: number
  }

  /** Timestamp of last save */
  lastSaved?: number
}

// ─── Default Config ───────────────────────────────────────────────────────────

const DEFAULT_CONFIG: GovernanceConfigOverrides = {
  version: 1,
  qualityGates: {
    minCogLevel: 2,
    maxCogLevel: 5,
    minDidacticDepth: 2,
    minQualityScore: 70,
  },
  pipelineStages: {
    schemaEnabled: true,
    typeEnabled: true,
    contentEnabled: true,
    supportEnabled: true,
    visualEnabled: true,
    graphEnabled: true,
    pedagogyEnabled: true,
  },
  pipelineBehavior: {
    strictMode: false,
    autoBlock: true,
  },
  policy: {
    minQuestionsPerSkill: 5,
    maxQuestionsPerSkill: 50,
    requireHints: true,
    requireExplanation: true,
    requireVisuals: false,
    distributionCheck: true,
  },
  // FG4 Fase A — full-coverage shadow ramp.
  // ALL content modules are now on the observability lane: pedagogy findings
  // are recorded in shadowFindingsStore and NEVER block the gate (zero
  // production impact). This yields the complete impact-report across every
  // module before any 'warn'/'enforce' decision (Plan §FG4 Fase 1 / Audit
  // §FG4 Fase A — "shadow voor alle modules"). 'shadow' is deliberately
  // chosen over 'enforce' here: it surfaces what WOULD block without the
  // cliff-edge of taking valid-but-undertagged questions offline.
  //
  // Ramp history:
  //   - REK first promoted 2 juni 2026 (GDLA-01).
  //   - Remaining modules added 10 juni 2026 — operator confirmed low risk;
  //     shadow cannot block, so this is a telemetry-only expansion.
  // Next step (per-module, deliberate): promote a module 'shadow' → 'warn'
  // → 'enforce' only after its shadow-findings are triaged and remediated.
  // FG4 Fase 2 — first ramp (REK). REK was the first module on the shadow lane
  // and its findings are the most triaged, so it is the canonical first
  // promotion. 'warn' is non-blocking: blocker findings degrade to coach-level
  // (see applyEnforcementModes §warn), surfacing REK pedagogy issues as
  // visible-but-non-gating warnings WITHOUT taking any question offline. Fully
  // reversible — revert to 'shadow' to drop back to telemetry-only.
  enforcement: {
    pedagogical: {
      REK: 'warn',
      GET: 'shadow',
      GELD: 'shadow',
      TIJD: 'shadow',
      MET: 'shadow',
      SPR: 'shadow',
      SWW: 'shadow',
      TAA: 'shadow',
      STU: 'shadow',
      TOPO: 'shadow',
    },
    schemas: {},
  },
  // FG4 — first grace-ledger entry (closes V-10: the mechanism was built and
  // tested but had no live entries). A grace entry is a NAMED, OWNED, EXPIRING
  // exception. This one documents the remaining REK author-needed pedagogy
  // findings that are not yet remediated: while REK ramps to 'warn' (and later
  // 'enforce'), these matching blockers degrade to coach-level (severity 50)
  // until the owner closes them by the `until` date. Visible in the admin
  // GraceLedgerSection so the debt is countable and has a deadline.
  graceLedger: [
    {
      scope: { module: 'REK' },
      until: '2026-09-30',
      reason:
        'REK ramped to warn (FG4 Fase 2). Remaining author-needed pedagogy findings are being triaged; degrade matching blockers to coach-level until remediation closes, then promote REK to enforce.',
      owner: 'governance-lead',
      degradeToSeverity: 50,
    },
  ],
  notifications: {
    notifyBlockers: true,
    notifyWarnings: false,
    notifyWeeklyReport: true,
    notifyNewRules: true,
  },
  system: {
    cacheEnabled: true,
    debugMode: false,
    autoValidateOnSave: true,
    batchSize: 100,
  },
}

// ─── Store Operations ─────────────────────────────────────────────────────────

/**
 * Load governance config from dataLayer.
 * Returns default config if nothing is stored or if stored data is invalid.
 */
export async function loadGovernanceConfig(): Promise<GovernanceConfigOverrides> {
  try {
    const parsed = await dataLayer.getGovernanceConfig()
    if (!parsed) return { ...DEFAULT_CONFIG }

    // Version check
    if (parsed.version !== 1) {
      warn('Governance config version mismatch, using defaults')
      return { ...DEFAULT_CONFIG }
    }

    // Merge with defaults to fill any missing fields
    return mergeWithDefaults(parsed as Partial<GovernanceConfigOverrides>)
  } catch (e) {
    warn('Failed to load governance config, using defaults')
    return { ...DEFAULT_CONFIG }
  }
}

/**
 * Save governance config via dataLayer.
 */
export async function saveGovernanceConfig(
  config: GovernanceConfigOverrides,
): Promise<boolean> {
  try {
    const toSave: GovernanceConfigOverrides = {
      ...config,
      version: 1,
      lastSaved: Date.now(),
    }
    const success = await dataLayer.saveGovernanceConfig(
      toSave as Record<string, unknown>,
    )
    if (success) {
      debug('✅ Governance config saved')
    }
    return success !== false
  } catch (e) {
    warn('Failed to save governance config')
    return false
  }
}

/**
 * Reset governance config to defaults via dataLayer.
 */
export async function resetGovernanceConfig(): Promise<GovernanceConfigOverrides> {
  await dataLayer.resetGovernanceConfig()
  debug('🔄 Governance config reset to defaults')
  return { ...DEFAULT_CONFIG }
}

/**
 * Get the default config (for reset operations).
 */
export function getDefaultGovernanceConfig(): GovernanceConfigOverrides {
  return { ...DEFAULT_CONFIG }
}

/**
 * Get the list of disabled pipeline stages from config.
 * Returns the subset of stage numbers (1-5) that should be skipped.
 *
 * Notes:
 *  - Stage 6 (scoring) and 7 (gate) are not skippable — they always run.
 *  - `pedagogyEnabled` is a sub-toggle of stage 3 and is NOT mapped here;
 *    consumers read it directly and pass it as `PipelineOptions.skipPedagogy`
 *    so the pedagogy sub-stage can be toggled without disabling content.
 *  - `typeEnabled` and `schemaEnabled` both share stage 1; toggling type
 *    alone is therefore a no-op while schema is on (by design — schema and
 *    type form the structural-validation pair).
 */
export function getDisabledStages(config: GovernanceConfigOverrides): number[] {
  const stages = config.pipelineStages
  if (!stages) return []

  const disabled: number[] = []
  if (stages.schemaEnabled === false) disabled.push(STAGES.SCHEMA)
  if (stages.graphEnabled === false) disabled.push(STAGES.GRAPH)
  if (stages.contentEnabled === false) disabled.push(STAGES.CONTENT)
  if (stages.visualEnabled === false) disabled.push(STAGES.VISUAL)
  if (stages.supportEnabled === false) disabled.push(STAGES.SUPPORT)
  // typeEnabled is part of stage 1 (schema+type pair)
  if (stages.typeEnabled === false) disabled.push(STAGES.TYPE)

  return [...new Set(disabled)]
}

// ─── Enforcement & Grace Resolvers (FG4 §F0.C) ──────────────────────────────

/**
 * Resolve the pedagogical enforcement mode for a module. Falls back to
 * `'enforce'` when unconfigured.
 *
 * STRUCTURAL CONTRACT (audit feedback after S4 wiring):
 *   - 'off' MUST be an explicit opt-in — never the silent default. If
 *     `'off'` were the default, supplying `enforcementConfig` to the
 *     orchestrator would silently drop every pedagogy issue (since
 *     applyEnforcementModes treats 'off' as "drop, no telemetry"). That
 *     is exactly the cliff-edge regression FG4 is trying to prevent.
 *   - Therefore: unconfigured scope === "keep current behaviour" === 'enforce'.
 *     Operators move a scope to 'shadow' / 'warn' / 'off' deliberately via
 *     the Settings UI; they never enter those modes accidentally.
 */
export function resolvePedagogicalMode(
  config: GovernanceConfigOverrides,
  module: string,
): EnforcementMode {
  return config.enforcement?.pedagogical?.[module] ?? 'enforce'
}

/**
 * Resolve the strict-schema enforcement mode for a question type. Falls back
 * to `'enforce'` when unconfigured (same contract as resolvePedagogicalMode).
 */
export function resolveSchemaMode(
  config: GovernanceConfigOverrides,
  questionType: string,
): EnforcementMode {
  return config.enforcement?.schemas?.[questionType] ?? 'enforce'
}

/**
 * Find an active (non-expired) grace ledger entry matching the given scope.
 * The most specific match wins: questionId > family > module.
 */
export function findActiveGrace(
  config: GovernanceConfigOverrides,
  scope: { module?: string; family?: string; questionId?: string },
  now: number = Date.now(),
): GraceLedgerEntry | undefined {
  const ledger = config.graceLedger ?? []
  const active = ledger.filter((e) => {
    const until = Date.parse(e.until)
    if (Number.isFinite(until) && until < now) return false
    if (e.scope.questionId && e.scope.questionId === scope.questionId)
      return true
    if (e.scope.family && e.scope.family === scope.family) return true
    if (
      e.scope.module &&
      e.scope.module === scope.module &&
      !e.scope.family &&
      !e.scope.questionId
    )
      return true
    return false
  })
  // Specificity ranking
  return active.sort((a, b) => specificity(b.scope) - specificity(a.scope))[0]
}

function specificity(scope: GraceLedgerEntry['scope']): number {
  if (scope.questionId) return 3
  if (scope.family) return 2
  if (scope.module) return 1
  return 0
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function mergeWithDefaults(
  stored: Partial<GovernanceConfigOverrides>,
): GovernanceConfigOverrides {
  return {
    version: 1,
    qualityGates: {
      ...DEFAULT_CONFIG.qualityGates,
      ...stored.qualityGates,
    },
    pipelineStages: {
      ...DEFAULT_CONFIG.pipelineStages,
      ...stored.pipelineStages,
    },
    pipelineBehavior: {
      ...DEFAULT_CONFIG.pipelineBehavior,
      ...stored.pipelineBehavior,
    },
    policy: {
      ...DEFAULT_CONFIG.policy,
      ...stored.policy,
    },
    enforcement: {
      pedagogical: {
        ...DEFAULT_CONFIG.enforcement?.pedagogical,
        ...stored.enforcement?.pedagogical,
      },
      schemas: {
        ...DEFAULT_CONFIG.enforcement?.schemas,
        ...stored.enforcement?.schemas,
      },
    },
    graceLedger: stored.graceLedger ?? DEFAULT_CONFIG.graceLedger ?? [],
    notifications: {
      ...DEFAULT_CONFIG.notifications,
      ...stored.notifications,
    },
    system: {
      ...DEFAULT_CONFIG.system,
      ...stored.system,
    },
    lastSaved: stored.lastSaved,
  }
}
